import { api } from './client';

/*  Specchio dei contratti della stampa.

    Il documento non si conserva: si ricompone leggendo il preventivo ogni volta. Quindi
    qui non c'è nessun «apri l'ultima stampa» — non esiste un'ultima stampa, esiste il
    preventivo e il modello attivo di adesso.  */

export interface CampoStampabile {
  nome: string;
  /** A cosa serve, in una riga: è quello che legge chi scrive il modello. */
  cosa: string;
}

export interface ElencoStampabile {
  nome: string;
  cosa: string;
  colonne: CampoStampabile[];
}

export interface DizionarioStampa {
  campi: CampoStampabile[];
  elenchi: ElencoStampabile[];
  condizioni: CampoStampabile[];
}

export interface ModelloLetto {
  id: string;
  tipo: string;
  nomeFile: string;
  versione: number;
  attivo: boolean;
  nota: string | null;
  byte: number;
  caricatoIl: string;
  caricatoDa: string;
}

/** Falso quando LibreOffice non è installato sulla macchina dell'API. */
export interface PossibilitaStampa {
  pdf: boolean;
}

export interface OpzioniDiStampa {
  prezzi: boolean;
  /*  I prezzi solo sui totali di capitolo e nel riepilogo, le voci senza (15/10/2026).
      Conta solo con `prezzi` acceso.  */
  soloTotali?: boolean;
  articoli: boolean;
  /*  Chi ha scritto l'offerta e la sua firma. Si spegne quando a mandarla è un altro:
      «ho fatto un preventivo per Novara e non ho messo il mio nome, perché poi
      l'avrebbe mandato via Monica».  */
  redattore: boolean;
  formato: 'pdf' | 'docx';
  /*  Stampa anche con lo stesso articolo a prezzi diversi, che altrimenti ferma il
      server. Non e' un'impostazione del documento come le altre tre: e' una decisione
      presa su questa stampa, e chi la prende resta scritto nel registro.  */
  scavalco?: boolean;
}

/*  Fa scendere il file dal browser. Serve un collegamento finto perché il contenuto
    arriva da fetch — l'identità viaggia in un'intestazione — e un blob non si apre da
    solo. L'URL temporaneo si revoca subito: altrimenti il blob resta in memoria finché
    non si chiude la scheda, e su un PDF da qualche mega, dieci stampe si sentono.  */
function fallaScendere(contenuto: Blob, nomeFile: string) {
  const indirizzo = URL.createObjectURL(contenuto);
  const collegamento = document.createElement('a');

  collegamento.href = indirizzo;
  collegamento.download = nomeFile;
  document.body.appendChild(collegamento);
  collegamento.click();
  collegamento.remove();

  URL.revokeObjectURL(indirizzo);
}

export const stampa = {
  dizionario: () => api.get<DizionarioStampa>('/stampa/dizionario'),

  possibilita: () => api.get<PossibilitaStampa>('/stampa/possibilita'),

  modelli: () => api.get<ModelloLetto[]>('/stampa/modelli'),

  caricaModello: (file: File, nota?: string) =>
    api.carica<ModelloLetto>(
      `/stampa/modelli${nota ? `?nota=${encodeURIComponent(nota)}` : ''}`,
      file,
    ),

  attivaModello: (id: string) => api.post<ModelloLetto>(`/stampa/modelli/${id}/attivazione`, {}),

  scaricaModello: async (id: string) => {
    const file = await api.scarica(`/stampa/modelli/${id}/file`);
    fallaScendere(file.contenuto, file.nomeFile);
  },

  /*  Compone l'offerta e la restituisce SENZA farla scendere.

      Separare il comporre dal salvare e' quello che permette l'anteprima chiesta in
      riunione: si guarda quello che e' stato composto e, se va bene, si salva
      esattamente quei byte — non una seconda composizione che potrebbe uscire diversa
      perche' nel frattempo qualcuno ha toccato il preventivo o il modello.

      Costa anche una conversione in meno: LibreOffice ci mette un paio di secondi, e
      farlo due volte per lo stesso documento si sente.  */
  componi: async (id: string, opzioni: OpzioniDiStampa) => {
    const parametri = new URLSearchParams({
      formato: opzioni.formato,
      prezzi: String(opzioni.prezzi),
      articoli: String(opzioni.articoli),
      redattore: String(opzioni.redattore),
      ...(opzioni.soloTotali === true ? { totali: 'true' } : {}),
      ...(opzioni.scavalco === true ? { scavalco: 'true' } : {}),
    });

    return api.scarica(`/stampa/preventivi/${id}?${parametri}`);
  },

  /** Compone l'offerta e la fa scendere. Torna i segnaposto che il modello chiede e nessuno pubblica. */
  preventivo: async (id: string, opzioni: OpzioniDiStampa): Promise<string[]> => {
    const file = await stampa.componi(id, opzioni);
    fallaScendere(file.contenuto, file.nomeFile);

    return file.sconosciuti;
  },

  /** Salva quello che si sta guardando: gli stessi byte, senza ricomporre. */
  salva: (contenuto: Blob, nomeFile: string) => fallaScendere(contenuto, nomeFile),
};
